import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebaseConfig';
import { User, generateId } from '../services/firebaseService';

// Types
export interface SuggestedAction {
  type: string;
  payload: any;
  approved: boolean;
  executed: boolean;
  dismissed?: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  suggestedActions?: SuggestedAction[];
  timestamp: any;
}

interface ChatContextType {
  messages: ChatMessage[];
  loading: boolean;
  sending: boolean;
  aiConsented: boolean | null;
  sendMessage: (content: string, userData: any) => Promise<void>;
  approveAction: (messageId: string, actionIndex: number) => Promise<SuggestedAction | null>;
  dismissAction: (messageId: string, actionIndex: number) => Promise<void>;
  clearChat: () => Promise<void>;
  acceptAiConsent: () => void;
  revokeAiConsent: () => void;
}

// Exported so the chat screen can be previewed against a hand-built
// conversation -- see src/preview/. Only ChatProvider should provide it.
export const ChatContext = createContext<ChatContextType | null>(null);

// Parse ACTION blocks from AI response
function parseActions(content: string): { cleanContent: string; actions: SuggestedAction[] } {
  const actions: SuggestedAction[] = [];

  const closedRegex = /\[ACTION:([\w_]+)\]([\s\S]*?)\[\/ACTION\]/g;
  const openRegex = /\[ACTION:([\w_]+)\](\{[^[]*?\})/g;

  let match;
  const closedMatches = new Set<number>();
  while ((match = closedRegex.exec(content)) !== null) {
    closedMatches.add(match.index);
    try {
      const type = match[1];
      const payload = JSON.parse(match[2].trim());
      actions.push({ type, payload, approved: false, executed: false });
    } catch (e) {
      console.error('Failed to parse action:', match[0]);
    }
  }

  while ((match = openRegex.exec(content)) !== null) {
    if (closedMatches.has(match.index)) continue;
    try {
      const type = match[1];
      const payload = JSON.parse(match[2].trim());
      actions.push({ type, payload, approved: false, executed: false });
    } catch (e) {
      console.error('Failed to parse action:', match[0]);
    }
  }

  const cleanContent = content.replace(closedRegex, '').replace(openRegex, '').trim();
  return { cleanContent, actions };
}

// Build system prompt with user data context
function buildSystemPrompt(userData: any): string {
  const nameMap: Record<string, string> = {};
  if (userData.userId) nameMap[userData.userId] = 'You';
  userData.connections?.forEach((c: any) => {
    if (c.uid) nameMap[c.uid] = c.displayName || c.email || c.uid;
  });
  const resolveName = (uid: string) => nameMap[uid] || uid;

  const taskList = userData.tasks?.map((t: any) => {
    const assignedLabel = t.assignedTo
      ? (t.assignedTo === userData.userId ? 'assignedTo: You' : `assignedTo: ${resolveName(t.assignedTo)}`)
      : '';
    const watcherNames = t.watchers?.length
      ? `watchers: [${t.watchers.map(resolveName).join(', ')}]`
      : '';
    return `- [${t.id}] "${t.title}" (${t.completed ? 'COMPLETED' : `status: ${t.status}`}, category: ${t.category}${t.color ? `, color: ${t.color}` : ''}${assignedLabel ? `, ${assignedLabel}` : ''}${watcherNames ? `, ${watcherNames}` : ''}${t.progress ? `, progress: ${t.progress}%` : ''}${t.startDateTime ? `, start: ${t.startDateTime}` : ''}${t.endDateTime ? `, end: ${t.endDateTime}` : ''}${t.deadline ? `, deadline: ${t.deadline}` : ''}${t.reminder ? `, reminder: ${t.reminder}` : ''}${t.dependencies?.length ? `, depends on: [${t.dependencies.join(', ')}]` : ''}${t.ganttChartIds?.length ? `, charts: [${t.ganttChartIds.join(', ')}]` : ''}${t.notes ? `, notes: "${t.notes}"` : ''})`;
  }).join('\n') || 'No tasks';

  const ganttChartList = userData.ganttCharts?.map((c: any) =>
    `- [${c.id}] "${c.name}"`
  ).join('\n') || 'No gantt charts';

  const habitList = userData.habits?.map((h: any) =>
    `- [${h.id}] "${h.title}" (streak: ${h.streak || 0}${h.lastCompleted ? `, last completed: ${h.lastCompleted}` : ''})`
  ).join('\n') || 'No habits';

  const connectionsList = userData.connections?.map((c: any) =>
    `- "${c.displayName || c.email}" (userId: ${c.uid})`
  ).join('\n') || 'No connections';

  const now = new Date();
  const currentDateTime = now.toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
  });

  return `You are Sensei AI, the taskDojo productivity companion. You help users manage their tasks, habits, and overall wellbeing.

Current date/time: ${currentDateTime}
Current user: ${userData.displayName || 'User'}
Current user ID: ${userData.userId}

== TASKS (up to 50 most recent) ==
${taskList}

== HABITS ==
${habitList}

== CONNECTIONS (users tasks can be assigned to or shared with) ==
${connectionsList}

== GANTT CHARTS ==
${ganttChartList}

== TASK CATEGORIES ==
Default: ${userData.defaultCategories?.join(', ') || 'General, Work, Personal, Health, Learning, Finance, Other'}
Custom: ${userData.customCategories?.map((c: any) => c.name).join(', ') || 'None'}

== TASK COLORS ==
Purple: #9333EA, Blue: #3B82F6, Green: #10B981, Orange: #F59E0B, Red: #EF4444, Pink: #EC4899, Teal: #14B8A6, Indigo: #6366F1

You can suggest actions for the user. When you want to suggest an action, include it in your response using this format. The user must approve each action before it is executed.

=== Task actions ===
[ACTION:create_task]{"title":"Task title","category":"Work","notes":"Optional notes","startDateTime":"2024-12-24T09:00:00.000Z","endDateTime":"2024-12-24T17:00:00.000Z","deadline":"2024-12-25T17:00:00.000Z","reminder":"2024-12-25T09:00:00.000Z","color":"#3B82F6","assignedTo":"userId_from_connections","dependencies":["task_id_1"],"ganttChartIds":["chart_id_1"]}[/ACTION]
[ACTION:update_task]{"taskId":"task_123","title":"New title","notes":"Updated notes","category":"Work","startDateTime":"2024-12-25T09:00:00.000Z","endDateTime":"2024-12-25T17:00:00.000Z","deadline":"2024-12-25T17:00:00.000Z","status":"in_progress","progress":50,"reminder":"2024-12-25T09:00:00.000Z","color":"#EF4444","assignedTo":"userId"}[/ACTION]
[ACTION:complete_task]{"taskId":"task_123"}[/ACTION]
[ACTION:delete_task]{"taskId":"task_123"}[/ACTION]

=== Habit actions ===
[ACTION:create_habit]{"title":"Habit title","icon":"💪","color":"#4ADE80","description":"Optional description"}[/ACTION]
[ACTION:update_habit]{"habitId":"habit_123","title":"New title"}[/ACTION]
[ACTION:delete_habit]{"habitId":"habit_123"}[/ACTION]

=== Gantt chart actions ===
[ACTION:create_gantt_chart]{"name":"Chart name"}[/ACTION]
[ACTION:add_task_to_chart]{"taskId":"task_123","chartId":"chart_123"}[/ACTION]
[ACTION:remove_task_from_chart]{"taskId":"task_123","chartId":"chart_123"}[/ACTION]

=== Dependency actions ===
[ACTION:add_dependency]{"taskId":"task_123","dependsOnTaskId":"task_456"}[/ACTION]
[ACTION:remove_dependency]{"taskId":"task_123","dependsOnTaskId":"task_456"}[/ACTION]

=== Category actions ===
[ACTION:create_category]{"name":"Category name"}[/ACTION]
[ACTION:delete_category]{"name":"Category name"}[/ACTION]

Guidelines:
- Be conversational, encouraging, and supportive
- Reference the user's actual data to give personalized advice
- You CANNOT execute actions directly. ALL changes require ACTION blocks that the user must approve first.
- NEVER use past tense like "I've created", "I've marked", "All set!", "Done!" in your message text. The actions have NOT happened yet — they only happen after the user approves each ACTION block. Instead say things like "Here are the actions for you to approve:" or "I've prepared these changes for your approval:"
- CRITICAL: ACTION blocks MUST always use the exact format [ACTION:type]{...}[/ACTION] with the closing tag. Never omit [/ACTION]. Never embed ACTION blocks inside bullet points, lists, or inline text. Place each ACTION block on its own line, separate from conversational text.
- When greeting users or making suggestions WITHOUT performing actions, just write plain text. Do NOT include ACTION blocks as examples or suggestions — only use them when you are actually proposing a specific action for the user to approve.
- For batch requests (e.g. "mark all tasks as done"), include a SEPARATE ACTION block for EACH individual item
- Every single modification MUST have a corresponding ACTION block. If there is no ACTION block, the action will NOT happen. Never describe doing something without the ACTION block.
- For update_task, only include the fields that need changing (plus the taskId)
- Tasks support startDateTime, endDateTime (scheduled work window) and deadline (due date). All are ISO datetime strings. Use these when the user mentions scheduling.
- Tasks support a "reminder" field (ISO datetime) for push notification reminders.
- Tasks support a "color" field (hex code from the TASK COLORS list). When the user mentions a color, use the matching hex code.
- Tasks support a "progress" field (integer 0-100). Use with update_task to set completion percentage.
- Valid task statuses: not_started, in_progress, paused, completed, draft, declined.
- To assign a task to someone, use create_task or update_task with "assignedTo" set to their userId from the CONNECTIONS list. To assign to the current user, use the Current user ID above, or simply omit the "assignedTo" field (it defaults to the current user).
- If the user asks to assign a task to someone not in their connections, let them know they need to add that person as a connection first
- When creating or updating tasks with startDateTime/endDateTime, check existing tasks for time overlaps. If a clash exists, briefly warn the user.
- Gantt charts group tasks visually. A task can belong to multiple charts.
- IMPORTANT: When creating NEW tasks that should be on a Gantt chart, include "ganttChartIds" directly in the create_task payload (e.g. "ganttChartIds":["chart_id"]). Do NOT use separate add_task_to_chart actions for newly created tasks — that will fail because the task doesn't exist yet. Only use add_task_to_chart/remove_task_from_chart for EXISTING tasks.
- Tasks support a "dependencies" field — an array of task IDs that must complete before this task can begin. Use add_dependency/remove_dependency to manage individual dependency relationships.
- When the user says "A depends on B", use add_dependency with taskId=A's ID and dependsOnTaskId=B's ID. Never create circular dependencies.
- Keep responses concise but helpful
- For destructive actions (delete), explain why you're suggesting it`;
}

// Provider
interface ChatProviderProps {
  children: React.ReactNode;
  user: User | null;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, user }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiConsented, setAiConsented] = useState<boolean | null>(null);

  // Load consent from localStorage
  useEffect(() => {
    if (!user) {
      setAiConsented(null);
      return;
    }
    const val = localStorage.getItem(`ai_consent_${user.uid}`);
    setAiConsented(val === 'true');
  }, [user]);

  const acceptAiConsent = useCallback(() => {
    if (!user) return;
    localStorage.setItem(`ai_consent_${user.uid}`, 'true');
    setAiConsented(true);
  }, [user]);

  const revokeAiConsent = useCallback(() => {
    if (!user) return;
    localStorage.setItem(`ai_consent_${user.uid}`, 'false');
    setAiConsented(false);
  }, [user]);

  // Real-time listener for chat messages
  useEffect(() => {
    if (!user) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'users', user.uid, 'chatMessages'),
      orderBy('timestamp', 'asc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as ChatMessage));
        setMessages(data);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsubscribe;
  }, [user]);

  const sendMessage = useCallback(
    async (content: string, userData: any) => {
      if (!user || sending) return;
      setSending(true);

      try {
        const userMsgId = generateId('msg', user.uid);
        await setDoc(doc(db, 'users', user.uid, 'chatMessages', userMsgId), {
          id: userMsgId,
          role: 'user',
          content,
          timestamp: serverTimestamp(),
        });

        const recentMessages = messages.slice(-20).map((m) => ({
          role: m.role,
          content: m.content,
        }));
        recentMessages.push({ role: 'user', content });

        const functions = getFunctions();
        const sendAIMessage = httpsCallable(functions, 'sendAIMessage');
        const result = await sendAIMessage({
          messages: recentMessages,
          systemPrompt: buildSystemPrompt(userData),
        });

        const data = result.data as any;
        const assistantContent = data.content || 'Sorry, I had trouble responding. Please try again.';
        if (data.remaining) {
          console.log(`AI quota remaining: ${data.remaining.messages} messages, ${data.remaining.tokens} tokens`);
        }
        const { cleanContent, actions } = parseActions(assistantContent);

        const assistantMsgId = generateId('msg', user.uid);
        await setDoc(doc(db, 'users', user.uid, 'chatMessages', assistantMsgId), {
          id: assistantMsgId,
          role: 'assistant',
          content: cleanContent,
          suggestedActions: actions.length > 0 ? actions : null,
          timestamp: serverTimestamp(),
        });
      } catch (error: any) {
        console.error('Error sending message:', error);
        const isQuotaExceeded = error?.code === 'functions/resource-exhausted';
        const errorContent = isQuotaExceeded
          ? error.message
          : 'Sorry, I encountered an error. Please try again.';
        const errorMsgId = generateId('msg', user.uid);
        await setDoc(doc(db, 'users', user.uid, 'chatMessages', errorMsgId), {
          id: errorMsgId,
          role: 'assistant',
          content: errorContent,
          timestamp: serverTimestamp(),
        });
      } finally {
        setSending(false);
      }
    },
    [user, sending, messages]
  );

  const approveAction = useCallback(
    async (messageId: string, actionIndex: number): Promise<SuggestedAction | null> => {
      if (!user) return null;
      const message = messages.find((m) => m.id === messageId);
      if (!message?.suggestedActions?.[actionIndex]) return null;

      const action = { ...message.suggestedActions[actionIndex] };
      action.approved = true;
      action.executed = true;

      const updatedActions = [...(message.suggestedActions || [])];
      updatedActions[actionIndex] = action;
      await setDoc(
        doc(db, 'users', user.uid, 'chatMessages', messageId),
        { suggestedActions: updatedActions },
        { merge: true }
      );

      return action;
    },
    [user, messages]
  );

  const dismissAction = useCallback(
    async (messageId: string, actionIndex: number) => {
      if (!user) return;
      const message = messages.find((m) => m.id === messageId);
      if (!message?.suggestedActions?.[actionIndex]) return;

      const action = { ...message.suggestedActions[actionIndex] };
      action.dismissed = true;

      const updatedActions = [...(message.suggestedActions || [])];
      updatedActions[actionIndex] = action;
      await setDoc(
        doc(db, 'users', user.uid, 'chatMessages', messageId),
        { suggestedActions: updatedActions },
        { merge: true }
      );
    },
    [user, messages]
  );

  const clearChat = useCallback(async () => {
    if (!user) return;
    try {
      const chatRef = collection(db, 'users', user.uid, 'chatMessages');
      const snapshot = await getDocs(chatRef);
      const deletions = snapshot.docs.map((d) =>
        deleteDoc(doc(db, 'users', user.uid, 'chatMessages', d.id))
      );
      await Promise.all(deletions);
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
  }, [user]);

  return (
    <ChatContext.Provider
      value={{
        messages,
        loading,
        sending,
        aiConsented,
        sendMessage,
        approveAction,
        dismissAction,
        clearChat,
        acceptAiConsent,
        revokeAiConsent,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
